import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Music, Play } from 'lucide-react'

import type { SilkAsset } from '@/api/types'
import { resolveMediaDisplayUrl } from '@/features/studio/lib/media-display'
import { ImagePreviewModal } from '@/features/studio/modals/ImagePreviewModal'
import { MediaPreviewModal } from '@/features/studio/modals/MediaPreviewModal'
import { AssetAudioWavePlayer } from '@/features/studio/pages/assets/AssetAudioWavePlayer'
import { studioCatalogCardShellClass } from '@/features/studio/components/StudioCatalogCard'
import { CatalogListCardContent } from '@/shared/components/catalog/CatalogListCardContent'
import { CATALOG_LIST_COVER_FALLBACK_ASPECT } from '@/shared/components/catalog/catalogListGrid'
import { ProtoBadge, ProtoThumb } from '@/features/studio/components/ui-kit'
import { Button } from '@/shared/components/ui/button'

function silkMediaCategory(asset: SilkAsset): 'image' | 'video' | 'audio' | 'other' {
  const mime = (asset.mime_type ?? '').toLowerCase()
  if (asset.kind === 'image' || mime.startsWith('image/')) return 'image'
  if (asset.kind === 'video' || mime.startsWith('video/')) return 'video'
  if (asset.kind === 'audio' || mime.startsWith('audio/')) return 'audio'
  return 'other'
}

function AssetThumbnail({ asset }: { asset: SilkAsset }) {
  const category = silkMediaCategory(asset)
  const rawUrl = asset.url
  const displayUrl = rawUrl ? resolveMediaDisplayUrl(rawUrl) : ''
  const [failed, setFailed] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)

  useEffect(() => {
    setFailed(false)
  }, [displayUrl])

  if (category === 'image' && displayUrl && !failed) {
    return (
      <>
        <Button
          type="button"
          variant="ghost"
          onClick={() => setPreviewOpen(true)}
          className="absolute inset-0 block h-full w-full overflow-hidden rounded-none p-0 bg-[#0e0e16] hover:bg-[#0e0e16]"
        >
          <img
            src={displayUrl}
            alt=""
            className="h-full w-full object-contain transition hover:scale-[1.02]"
            onError={() => setFailed(true)}
          />
        </Button>
        <ImagePreviewModal
          open={previewOpen}
          urls={rawUrl ? [rawUrl] : []}
          index={0}
          title={asset.filename}
          onClose={() => setPreviewOpen(false)}
          onIndexChange={() => {}}
        />
      </>
    )
  }

  if (category === 'video' && displayUrl) {
    return (
      <>
        <Button
          type="button"
          variant="ghost"
          onClick={() => setPreviewOpen(true)}
          className="absolute inset-0 block h-full w-full overflow-hidden rounded-none p-0 bg-[#0e0e16] hover:bg-[#0e0e16]"
          aria-label={asset.filename}
        >
          <video src={displayUrl} className="h-full w-full object-contain" muted preload="metadata" playsInline />
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/30">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-black/55 ring-1 ring-white/20">
              <Play className="ml-0.5 h-5 w-5 text-white" fill="currentColor" />
            </span>
          </span>
        </Button>
        <MediaPreviewModal
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          url={rawUrl ?? displayUrl}
          kind="video"
          title={asset.filename}
        />
      </>
    )
  }

  if (category === 'audio') {
    if (displayUrl) {
      return (
        <>
          <AssetAudioWavePlayer
            src={displayUrl}
            title={asset.filename}
            onError={() => setFailed(true)}
            onExpand={() => setPreviewOpen(true)}
          />
          <MediaPreviewModal
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            url={rawUrl ?? displayUrl}
            kind="audio"
            title={asset.filename}
          />
        </>
      )
    }
    return (
      <div className="flex h-full w-full items-center justify-center bg-[#0e0e16]">
        <Music className="h-10 w-10 text-zinc-600" />
      </div>
    )
  }

  return (
    <div className="h-full w-full overflow-hidden bg-[#0e0e16]">
      <ProtoThumb seed={asset.public_id || String(asset.id)} aspect="square" className="h-full w-full" flush />
    </div>
  )
}

export function CreativeAssetCard({
  asset,
  actions,
}: {
  asset: SilkAsset
  actions?: ReactNode
}) {
  const { t } = useTranslation()
  const kindKey = `assets.kind.${asset.kind}`

  return (
    <article className={studioCatalogCardShellClass('overflow-hidden text-left')}>
      <div className={`relative w-full ${CATALOG_LIST_COVER_FALLBACK_ASPECT} shrink-0 overflow-hidden`}>
        <AssetThumbnail asset={asset} />
      </div>
      <CatalogListCardContent
        className="!aspect-auto min-h-[72px]"
        title={asset.filename || asset.public_id}
        hideFooter
        footerBlock={
          <div className="space-y-1.5">
            <div className="flex flex-wrap gap-1">
              <ProtoBadge tone="indigo">{t(kindKey, { defaultValue: asset.kind })}</ProtoBadge>
            </div>
            {actions}
          </div>
        }
      />
    </article>
  )
}
